/**
 * Lollipop Chart Type Definition
 * Horizontal or vertical lines terminated by circles — clean alternative to bar charts
 */

import {
  colorControls,
  numCategoriesControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
  axisControlsHorizontal,
  labelRotationControl,
  truncateXAxisLabelsControl,
  legendControls,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  spineCode,
  finishCode,
  sortingCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornGridCode,
  seabornAxisLabelsCode,
  seabornSpineCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';

import { darkenColor, getColors, getPrimaryColorPair } from '../shared/palettes';
import { deploymentProfile } from '../../config/deploymentProfile';

// Generate dynamic sample data
function generateSampleData(n) {
  const models = [
    'Item A', 'Item B', 'Item C', 'Item D', 'Item E',
    'Item F', 'Item G', 'Item H', 'Item I', 'Item J',
  ];
  const data = [];
  for (let i = 0; i < Math.min(n, models.length); i++) {
    data.push({ name: models[i], value: parseFloat((78 + Math.random() * 18).toFixed(1)) });
  }
  return data;
}

const lollipop = {
  id: 'lollipop',
  name: 'Lollipop Chart',
  category: 'lollipop',
  description: 'Ranked comparisons with lines and dots',

  getSampleData: (config) => generateSampleData(config.numCategories || 6),

  sampleData: [
    { name: 'Item A', value: 94.3 },
    { name: 'Item B', value: 92.1 },
    { name: 'Item C', value: 93.8 },
    { name: 'Item D', value: 89.5 },
    { name: 'Item E', value: 88.2 },
    { name: 'Item F', value: 87.1 },
  ],

  defaultConfig: {
    externalColorMode: 'single',
    color: '#4C6EE6',
    useBluesPalette: false,
    useRedsPalette: false,
    useGreensPalette: false,
    numCategories: 6,
    outputFormat: 'matplotlib',
    showGrid: true,
    showValues: true,
    valueDecimals: 1,
    ylabel: 'Category',
    xlabel: 'Value',
    title: '',
    xMin: '',
    xMax: '',
    yMin: '',
    yMax: '',
    barOrdering: 'descending',
    dotSize: 10,
    lineThickness: 2,
    highlightTop: true,
    highlightColor: '#4C6EE6',
    orientation: 'horizontal',
    labelRotation: null,
    truncateXAxisLabels: false,
    showLegend: false,
    legendPosition: 'top',
    colorOverrides: {},
  },

  controls: [
    ...colorControls,
    {
      key: 'highlightTop',
      label: 'Highlight Top Item',
      type: 'toggle',
    },
    {
      key: 'highlightColor',
      label: 'Highlight Color',
      type: 'color',
      options: ['#4C6EE6', '#5DBF8A', '#C44B3D'],
    },
    numCategoriesControl,
    {
      key: 'orientation',
      label: 'Orientation',
      type: 'select',
      options: [
        { label: 'Horizontal', value: 'horizontal' },
        { label: 'Vertical', value: 'vertical' },
      ],
    },
    {
      key: 'barOrdering',
      label: 'Sort Order',
      type: 'select',
      options: [
        { label: 'Descending (High to Low)', value: 'descending' },
        { label: 'Ascending (Low to High)', value: 'ascending' },
        { label: 'Original Order', value: 'original' },
      ],
    },
    {
      key: 'dotSize',
      label: 'Dot Size',
      type: 'slider',
      min: 4,
      max: 20,
      step: 1,
      unit: 'px',
    },
    {
      key: 'lineThickness',
      label: 'Line Thickness',
      type: 'slider',
      min: 1,
      max: 5,
      step: 0.5,
      unit: 'px',
    },
    gridControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    showValuesControl,
    valueDecimalsControl,
    ...legendControls,
    ...axisControlsHorizontal,
  ],

  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value;
    const n = config.numCategories || 6;
    const isHorizontal = config.orientation !== 'vertical';

    // Sample data for code
    const models = ['Item A', 'Item B', 'Item C', 'Item D', 'Item E', 'Item F', 'Item G', 'Item H'];
    const categories = models.slice(0, n);
    const values = Array.from({ length: n }, () => (78 + Math.random() * 18).toFixed(1));

    const dotColor = getColors(config, 1, s)[0];
    const highlightColor = getPrimaryColorPair(config, s).highlight;
    const mutedColor = deploymentProfile.isExternal ? darkenColor(dotColor, 0.08) : dotColor;
    const mutedComment = deploymentProfile.isExternal ? 'a darker base color' : 'the base color';

    const dotSizePt = ((config.dotSize || 10) * 8);
    const highlightSizePt = dotSizePt * 4;
    const lineWidth = config.lineThickness || 2;
    const decimals = config.valueDecimals ?? 1;

    // Seaborn output
    if (config.outputFormat === 'seaborn') {
      return `${seabornSetup(s)}

# ======== DATA ========
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
categories = df['${dataContext.mapping.category}'].tolist()
values = df['${dataContext.mapping.value}'].tolist()` : `categories = ${JSON.stringify(categories)}
values = [${values.join(', ')}]`}
# ======================
${sortingCode(config, 'values', 'categories')}
${seabornCreateFigure(6, 5, 300, s)}

# Seaborn does not have a native lollipop chart; using matplotlib directly
${config.highlightTop ? `# Highlight colors: top item gets accent, rest get ${mutedComment}
dot_colors = ['${highlightColor}' if i == 0 else '${mutedColor}' for i in range(len(categories))]
dot_sizes = [${highlightSizePt} if i == 0 else ${dotSizePt} for i in range(len(categories))]
line_colors = ['${highlightColor}' if i == 0 else '${mutedColor}' for i in range(len(categories))]` : `dot_colors = ['${dotColor}'] * len(categories)
dot_sizes = [${dotSizePt}] * len(categories)
line_colors = ['${dotColor}'] * len(categories)`}

${isHorizontal ? `# Draw lollipop chart (horizontal)
ax.hlines(y=categories, xmin=0, xmax=values, colors=line_colors, linewidth=${lineWidth}, zorder=1)
ax.scatter(values, categories, s=dot_sizes, c=dot_colors, zorder=2, edgecolors='none')` : `# Draw lollipop chart (vertical)
ax.vlines(x=categories, ymin=0, ymax=values, colors=line_colors, linewidth=${lineWidth}, zorder=1)
ax.scatter(categories, values, s=dot_sizes, c=dot_colors, zorder=2, edgecolors='none')`}

${config.showValues ? (isHorizontal ? `# Add value labels
for i, (v, cat) in enumerate(zip(values, categories)):
    fw = 'bold' if ${config.highlightTop} and i == 0 else 'normal'
    ax.text(v + max(values)*0.02, cat, f'{v:.${decimals}f}', va='center', fontsize=10, fontweight=fw, color='${s?.foreground || '#000000'}')` : `# Add value labels
for i, (v, cat) in enumerate(zip(values, categories)):
    fw = 'bold' if ${config.highlightTop} and i == 0 else 'normal'
    ax.text(cat, v + max(values)*0.02, f'{v:.${decimals}f}', ha='center', fontsize=10, fontweight=fw, color='${s?.foreground || '#000000'}')`) : '# Value labels disabled'}

${config.highlightTop ? `# Bold label for highlighted item
ax.get_${isHorizontal ? 'y' : 'x'}ticklabels()[0].set_fontweight('bold')` : ''}
${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}
${seabornGridCode(config, isHorizontal ? 'x' : 'y', s)}
${seabornSpineCode(s)}
${seabornFinishCode('lollipop_chart')}`;
    }

    // Matplotlib output (default)
    return `${matplotlibSetup(s)}

# ======== DATA ========
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
categories = df['${dataContext.mapping.category}'].tolist()
values = df['${dataContext.mapping.value}'].tolist()` : `categories = ${JSON.stringify(categories)}
values = [${values.join(', ')}]`}
# ======================
${sortingCode(config, 'values', 'categories')}
${createFigure(6, 5, 300, s)}

${config.highlightTop ? `# Highlight colors: top item gets accent color, rest get ${mutedComment}
dot_colors = ['${highlightColor}' if i == 0 else '${mutedColor}' for i in range(len(categories))]
dot_sizes = [${highlightSizePt} if i == 0 else ${dotSizePt} for i in range(len(categories))]
line_colors = ['${highlightColor}' if i == 0 else '${mutedColor}' for i in range(len(categories))]` : `dot_colors = ['${dotColor}'] * len(categories)
dot_sizes = [${dotSizePt}] * len(categories)
line_colors = ['${dotColor}'] * len(categories)`}

${isHorizontal ? `# Draw lollipop chart (horizontal)
ax.hlines(y=categories, xmin=0, xmax=values, colors=line_colors, linewidth=${lineWidth}, zorder=1)
ax.scatter(values, categories, s=dot_sizes, c=dot_colors, zorder=2, edgecolors='none')` : `# Draw lollipop chart (vertical)
ax.vlines(x=categories, ymin=0, ymax=values, colors=line_colors, linewidth=${lineWidth}, zorder=1)
ax.scatter(categories, values, s=dot_sizes, c=dot_colors, zorder=2, edgecolors='none')`}

${config.showValues ? (isHorizontal ? `# Add value labels
for i, (v, cat) in enumerate(zip(values, categories)):
    fw = 'bold' if ${config.highlightTop} and i == 0 else 'normal'
    ax.text(v + max(values)*0.02, cat, f'{v:.${decimals}f}', va='center', fontsize=10, fontweight=fw, color='${s?.foreground || '#000000'}')` : `# Add value labels
for i, (v, cat) in enumerate(zip(values, categories)):
    fw = 'bold' if ${config.highlightTop} and i == 0 else 'normal'
    ax.text(cat, v + max(values)*0.02, f'{v:.${decimals}f}', ha='center', fontsize=10, fontweight=fw, color='${s?.foreground || '#000000'}')`) : '# Value labels disabled'}

${config.highlightTop ? `# Bold label for highlighted item
ax.get_${isHorizontal ? 'y' : 'x'}ticklabels()[0].set_fontweight('bold')` : ''}
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${gridCode(config, isHorizontal ? 'x' : 'y', s)}
${spineCode(s)}
${finishCode('lollipop_chart')}`;
  },
};

export default lollipop;
